import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BadgeCheck, Share2, MapPin, ArrowLeft, Trash2, Loader2, CalendarDays,
  Users, Ticket, Sparkles, CheckCircle2, XCircle, Edit3,
} from "lucide-react";

const SKILL_LABEL = {
  all_levels: "All levels",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export default function MeetupView() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/meetups/${id}`).then(r => setM(r.data)).catch(e => setErr(formatErr(e)));
    api.get(`/meetups/${id}/attendees`).then(r => setAttendees(r.data || [])).catch(() => {});
  };
  useEffect(load, [id]);

  const rsvp = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      await api.post(`/meetups/${id}/rsvp`);
      toast.success("You're going!");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try {
      await api.delete(`/meetups/${id}/rsvp`);
      toast.success("RSVP cancelled");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const share = async () => {
    const url = `${window.location.origin}/meetups/${id}`;
    try {
      if (navigator.share) { await navigator.share({ title: m?.title || "Meetup", url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { /* cancelled */ }
  };

  const remove = async () => {
    if (!confirm("Delete this meetup?")) return;
    try { await api.delete(`/meetups/${id}`); toast.success("Deleted"); nav("/meetups"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (err) return (
    <div className="max-w-lg mx-auto p-16 text-center">
      <p className="text-[#B84B4B] font-medium">{err}</p>
      <Link to="/meetups" className="btn-ghost inline-flex mt-4"><ArrowLeft size={14}/> Back to meetups</Link>
    </div>
  );
  if (!m) return <div className="p-16 text-center text-[#8A8A8A]"><Loader2 size={16} className="animate-spin inline"/> Loading…</div>;

  const owner = m.owner || {};
  const isHost = user && user.id === owner.id;
  const dt = new Date(m.starts_at);
  const dateStr = isNaN(dt.getTime()) ? m.starts_at : dt.toLocaleString(undefined, {
    weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", year: "numeric",
  });
  const endStr = m.ends_at ? new Date(m.ends_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" }) : null;
  const full = m.max_attendees && (m.attendee_count || 0) >= m.max_attendees;
  const past = !isNaN(dt.getTime()) && dt.getTime() < Date.now();

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="meetup-view">
      <Link to="/meetups" className="text-xs text-[#8A8A8A] hover:text-[#0A0A0A] inline-flex items-center gap-1 mb-4" data-testid="meetup-back">
        <ArrowLeft size={12}/> All meetups
      </Link>

      {m.image && (
        <div className="rounded-3xl overflow-hidden bg-[#F2F2F0] aspect-[3/1] mb-6" data-testid="meetup-cover">
          <img src={m.image} alt="" data-lightbox className="w-full h-full object-cover"/>
        </div>
      )}

      <p className="overline mb-2 inline-flex items-center gap-1.5">
        <Sparkles size={11}/> Meetup · {m.category} · {SKILL_LABEL[m.skill_level] || m.skill_level}
      </p>
      <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-tight" data-testid="meetup-title">{m.title}</h1>

      <div className="grid sm:grid-cols-2 gap-3 mt-5" data-testid="meetup-meta">
        <div className="card p-4">
          <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">When</p>
          <p className="text-sm font-medium mt-1 inline-flex items-center gap-1.5">
            <CalendarDays size={13} className="text-[#0057FF]"/>{dateStr}{endStr && ` – ${endStr}`}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Where</p>
          <p className="text-sm font-medium mt-1 inline-flex items-start gap-1.5">
            <MapPin size={13} className="text-[#0057FF] mt-0.5 flex-shrink-0"/>
            <span className="truncate">{m.location || "Location to be announced"}</span>
          </p>
        </div>
        <div className="card p-4">
          <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Price</p>
          <p className="text-sm font-medium mt-1 inline-flex items-center gap-1.5">
            <Ticket size={13} className="text-[#0057FF]"/>
            {m.is_paid ? `${m.currency} ${Number(m.price).toFixed(2)}` : "Free"}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Attendees</p>
          <p className="text-sm font-medium mt-1 inline-flex items-center gap-1.5">
            <Users size={13} className="text-[#0057FF]"/>
            {m.attendee_count || 0}{m.max_attendees ? ` / ${m.max_attendees}` : ""} going
          </p>
        </div>
      </div>

      {m.description && (
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-[#0A0A0A] mt-6" data-testid="meetup-description">
          {m.description}
        </p>
      )}

      {m.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4" data-testid="meetup-tags">
          {m.tags.map(t => <span key={t} className="badge-soft text-xs">#{t}</span>)}
        </div>
      )}

      {/* Real map */}
      {m.lat != null && m.lng != null && (
        <div className="mt-6" data-testid="meetup-map-block">
          <p className="text-xs font-medium text-[#4A4A4A] inline-flex items-center gap-1 mb-2">
            <MapPin size={12} className="text-[#0057FF]"/> On the map
          </p>
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E3]" data-testid="meetup-map">
            <iframe
              title="Meetup location"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${m.lng-0.01}%2C${m.lat-0.01}%2C${m.lng+0.01}%2C${m.lat+0.01}&layer=mapnik&marker=${m.lat}%2C${m.lng}`}
              width="100%" height="280"
              style={{ border: 0 }} loading="lazy"
            />
          </div>
          <a href={`https://www.openstreetmap.org/?mlat=${m.lat}&mlon=${m.lng}#map=15/${m.lat}/${m.lng}`}
             target="_blank" rel="noopener noreferrer"
             className="text-xs text-[#0057FF] hover:underline mt-1.5 inline-block" data-testid="meetup-map-open">
            Open in OpenStreetMap
          </a>
        </div>
      )}

      {/* Host card */}
      <div className="card p-5 mt-8 flex items-center gap-4" data-testid="meetup-host-card">
        {owner.avatar && <img src={owner.avatar} alt="" className="h-14 w-14 rounded-full object-cover bg-[#E5E5E3]"/>}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-[#8A8A8A] uppercase tracking-wide">Host</p>
          <p className="font-medium inline-flex items-center gap-1.5">
            <Link to={`/u/${owner.id}`} className="hover:underline">{owner.name}</Link>
            {owner.verified && <BadgeCheck size={13} className="text-[#0057FF]"/>}
          </p>
          <p className="text-xs text-[#8A8A8A]">{owner.category}{owner.location && ` · ${owner.location}`}</p>
        </div>
        <Link to={`/u/${owner.id}`} className="btn-ghost text-xs" data-testid="meetup-view-host">View profile</Link>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="meetup-actions">
        {!isHost && !past && m.status === "open" && (
          m.is_attending ? (
            <button onClick={cancel} disabled={busy} className="btn-secondary" data-testid="meetup-cancel-rsvp">
              <XCircle size={14}/> Cancel RSVP
            </button>
          ) : (
            <button onClick={rsvp} disabled={busy || full} className="btn-primary" data-testid="meetup-rsvp-btn">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <><CheckCircle2 size={14}/> {full ? "Full" : m.is_paid ? `I'm in — ${m.currency} ${Number(m.price).toFixed(0)}` : "I'm going"}</>}
            </button>
          )
        )}
        {past && <span className="text-xs text-[#8A8A8A]">This meetup has ended.</span>}
        {m.status === "cancelled" && <span className="text-xs text-[#B84B4B]">This meetup has been cancelled.</span>}
        <button onClick={share} className="btn-secondary" data-testid="meetup-share-btn"><Share2 size={14}/> Share</button>
        {isHost && (
          <Link to={`/meetups/new?edit=${m.id}`} className="btn-ghost text-sm" data-testid="meetup-edit-btn">
            <Edit3 size={13}/> Edit
          </Link>
        )}
        {isHost && (
          <button onClick={remove} className="btn-ghost text-[#B84B4B] text-sm" data-testid="meetup-delete-btn">
            <Trash2 size={12}/> Delete
          </button>
        )}
      </div>

      {m.is_paid && !isHost && !m.is_attending && (
        <p className="text-[11px] text-[#8A8A8A] mt-2">Paid meetup — the host will collect payment on arrival or via a link they share.</p>
      )}

      {/* Attendees */}
      {attendees.length > 0 && (
        <div className="mt-10" data-testid="meetup-attendees">
          <p className="text-xs font-medium text-[#4A4A4A] mb-3">Going ({attendees.length})</p>
          <div className="flex flex-wrap gap-3">
            {attendees.slice(0, 24).map(a => (
              <Link key={a.user.id} to={`/u/${a.user.id}`} className="inline-flex items-center gap-2 badge-soft" data-testid={`meetup-attendee-${a.user.id}`}>
                {a.user.avatar && <img src={a.user.avatar} alt="" className="h-5 w-5 rounded-full object-cover"/>}
                <span className="text-xs">{a.user.name}</span>
                {a.user.verified && <BadgeCheck size={10} className="text-[#0057FF]"/>}
              </Link>
            ))}
            {attendees.length > 24 && <span className="badge-soft text-xs">+{attendees.length - 24}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
